import {
  ChevronLeftIcon,
  PlusIcon,
  StarIcon,
  XMarkIcon
} from '@heroicons/react/20/solid';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { analyticsService } from 'src/services/analytics/analyticsService';
import { RFQ_BUILDER_IMAGE_SELECTION_COMPLETED } from 'src/services/analytics/rfqBuilderEvents';
import { captureException } from 'src/services/reporting';
import { useMediaQuery } from 'src/shared/hooks';
import { Rfq } from 'src/shared/types/rfq';
import { UploadedFile } from 'src/shared/types/uploadedFile';
import { isImageMimeType } from 'src/shared/utils/fileTypeUtils';

import {
  cn,
  Drawer,
  DrawerContent,
  Progress,
  Spinner
} from 'src/shared/components/ui';
import { ButtonV2, Input, Typography } from 'src/shared/components/ui';

import { updateRfqFileCaption } from '../../api/updateRfqFileCaption';
import { uploadRfqFileFromUrl } from '../../api/uploadRfqFileFromUrl';
import { useRfqConversationContext } from '../../context/RfqConversationContext';
import { useCaptionModal } from '../../hooks/useCaptionModal';
import { useFileCaptionManager } from '../../hooks/useFileCaptionManager';
import { useFilePreview } from '../../hooks/useFilePreview';
import useImageSearch from '../../hooks/useImageSearch';
import { useOptimisticFileUploadWithCaption } from '../../hooks/useOptimisticFileUploadWithCaption';
import BlobV2 from '../BlobV2';
import { CaptionModal } from './CaptionModal';
import RfqImageSelectionMobile from './RfqImageSelectionMobile';
import { UploadFailedNotice } from './UploadFailedNotice';

interface RfqImageSelectionProps {
  isOpen: boolean;
  onClose: () => void;
  rfq?: Rfq;
  /** Mode of operation: 'standalone' sends messages directly, 'builder' uses callback */
  mode?: 'standalone' | 'builder';
  /** Callback when images are selected in 'builder' mode */
  onImagesSelected?: (files: UploadedFile[]) => void;
  /** Callback when standalone send succeeds, to navigate to the next step */
  onSuccess?: () => void;
}

interface ImageSearchResult {
  url: string;
  thumbnail_url?: string;
  [key: string]: any;
}

interface SelectedImage {
  url: string;
  thumbnailUrl?: string; // Fallback URL if main URL fails
  id: string;
  file?: File; // Optional - only created when user adds caption or uploaded via file input
  pendingFileId: string;
  uploadedFileId?: string; // ID of uploaded file from backend (when uploaded via URL)
  uploadedFile?: UploadedFile; // Full uploaded file object for builder mode
  caption: string;
  originalIndex: number; // Store original index for filename
  isUploading?: boolean; // Track upload state for URL uploads
  uploadError?: string; // Track upload errors
  preview?: string; // Preview URL for file-based images
  source: 'user' | 'reference'; // Track file origin: 'user' for uploads, 'reference' for reference images
}

// Wrapper component that decides which version to render
const RfqImageSelection = ({
  mode = 'standalone',
  onSuccess,
  ...props
}: RfqImageSelectionProps) => {
  const isMobile = useMediaQuery();

  if (isMobile === null) {
    return null;
  }

  if (isMobile) {
    return (
      <RfqImageSelectionMobile {...props} mode={mode} onSuccess={onSuccess} />
    );
  }

  return (
    <RfqImageSelectionDesktop {...props} mode={mode} onSuccess={onSuccess} />
  );
};

// Desktop version of the component
const RfqImageSelectionDesktop = ({
  isOpen,
  onClose,
  rfq,
  mode = 'standalone',
  onImagesSelected,
  onSuccess
}: RfqImageSelectionProps) => {
  const [currentSearchQuery, setCurrentSearchQuery] = useState<
    string | undefined
  >(undefined);
  const [images, setImages] = useState<ImageSearchResult[]>([]);
  const [showMoreInput, setShowMoreInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(
    new Set()
  );
  const [loadedImageUrls, setLoadedImageUrls] = useState<Set<string>>(
    new Set()
  );
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [pendingImage, setPendingImage] = useState<SelectedImage | null>(null);
  const [pendingFileUploads, setPendingFileUploads] = useState<SelectedImage[]>(
    []
  );
  const lastRequestTimeRef = useRef<number>(0);
  const MIN_REQUEST_INTERVAL = 2000; // 2 seconds between requests
  const MIN_IMAGE_WIDTH = 100;
  const MIN_IMAGE_HEIGHT = 100;
  const selectedImagesRef = useRef<SelectedImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imagesContainerRef = useRef<HTMLDivElement>(null);
  const captionListRef = useRef<HTMLDivElement>(null);

  // Internal search term suffix (not shown to user)
  const isApparel = rfq?.rfq?.document?.category === 'apparel';
  const SEARCH_TERM_SUFFIX = isApparel
    ? 'site:zalando.co.uk'
    : 'product photography aesthetic';
  const llmSearchTerm = rfq?.rfq?.document?.image_search_term?.trim();

  // Strip any site:domain.tld prefix so it is never shown in the search input.
  const stripSitePrefix = (term: string) =>
    term.replace(/^site:\S+\s*/i, '').trim();

  const isFirstSearchRef = useRef(true);

  // Reusable hooks
  const { setPreview, getPreview, removePreview, previewUrlsRef } =
    useFilePreview();
  const uploadHook = useOptimisticFileUploadWithCaption({
    rfqId: rfq?.rfq?.id
  });
  const {
    pendingFiles,
    addFiles: addFilesToUpload,
    removeFile: removeFileFromUpload,
    waitForUploads,
    isUploading: isUploadingFiles,
    uploadFileWithCaption,
    retryUpload
  } = uploadHook;
  const { getPendingFileByIdOrFile, updateCaptionForFile } =
    useFileCaptionManager(rfq?.rfq?.id, uploadHook);
  const { connectToConversation, addUserMessage, isSending, setIsSending } =
    useRfqConversationContext();
  // Combine selectedImages with pendingImage and pendingFileUploads for modal display
  const modalItems = useMemo(() => {
    if (pendingImage) {
      return [pendingImage];
    }
    if (pendingFileUploads.length > 0) {
      return pendingFileUploads;
    }

    return selectedImages;
  }, [selectedImages, pendingImage, pendingFileUploads]);

  const captionModal = useCaptionModal(modalItems);
  const {
    isCaptionModalOpen,
    onCloseCaptionModal,
    currentCaptionIndex,
    captionValue,
    setCaptionValue,
    isEditMode,
    handleEditCaption,
    handleCaptionNext: captionNext,
    handleCaptionPrevious: captionPrevious,
    handleCaptionDone: captionDone,
    openModalForNewItems
  } = captionModal;

  const imageSearch = useImageSearch(currentSearchQuery);

  // Reset all state when drawer closes
  const resetState = useCallback(() => {
    setSelectedImages([]);
    setPendingImage(null);
    setPendingFileUploads([]);
    setShowMoreInput(false);
    setSearchQuery('');
    setRateLimitError(null);
    setCaptionValue('');
    isFirstSearchRef.current = true;
  }, [setCaptionValue]);

  // Wrapper for onClose that resets state
  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Helper to scroll images container to top
  const scrollToTop = useCallback(() => {
    if (!imagesContainerRef.current) return;

    let scrollableElement: HTMLElement | null = imagesContainerRef.current;

    // Find the nearest scrollable parent
    while (scrollableElement) {
      const hasScrollableContent =
        scrollableElement.scrollHeight > scrollableElement.clientHeight;
      const overflowY = window.getComputedStyle(scrollableElement).overflowY;

      if (
        hasScrollableContent &&
        (overflowY === 'auto' || overflowY === 'scroll')
      ) {
        scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });

        return;
      }
      scrollableElement = scrollableElement.parentElement;
    }
    // If no scrollable parent found, try scrolling the window
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Helper to check if a selected image is an actual image (not a document file)
  const isImageFile = (selectedImage: SelectedImage): boolean => {
    // If it has a file, check if it's an image type
    if (selectedImage.file) {
      return isImageMimeType(selectedImage.file.type);
    }

    // URL-based images from search are always images
    return true;
  };

  // Keep refs in sync
  useEffect(() => {
    selectedImagesRef.current = selectedImages;
    // Ensure all preview URLs are stored in the ref
    selectedImages.forEach((img) => {
      if (img.preview && !previewUrlsRef.current.has(img.id)) {
        setPreview(img.id, img.preview);
      }
    });
  }, [selectedImages, setPreview]);

  // Sync query results with images state
  useEffect(() => {
    if (imageSearch.data) {
      setImages(imageSearch.data);
      setFailedImageUrls(new Set());
      // Keep loaded URLs that are still in the new results (they're cached)
      const newImageUrls = new Set(
        imageSearch.data.map((img) => (typeof img === 'string' ? img : img.url))
      );

      setLoadedImageUrls((prev) => {
        const kept = new Set<string>();

        prev.forEach((url) => {
          if (newImageUrls.has(url)) {
            kept.add(url);
          }
        });

        return kept;
      });
      setRateLimitError(null);
      // Scroll to top when new search results are loaded
      setTimeout(scrollToTop, 100);
    } else if (imageSearch.isError) {
      setImages([]);
      setRateLimitError(null);
    }
  }, [imageSearch.data, imageSearch.isError, scrollToTop]);

  // Memoize fetchImages to prevent recreation
  const fetchImages = useCallback(
    (query: string) => {
      const trimmedQuery = query.trim();

      // Empty query: fall back to llmSearchTerm if available, otherwise bail
      if (!trimmedQuery && !llmSearchTerm) return;

      // Rate limiting: prevent requests if last request was too recent
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTimeRef.current;

      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;

        setRateLimitError(
          `Please wait ${Math.ceil(waitTime / 1000)} second(s) before searching again`
        );

        return;
      }

      lastRequestTimeRef.current = now;
      setRateLimitError(null);
      // Store the user's original search query for refine feature
      setUserSearchQuery(trimmedQuery);

      if (!trimmedQuery) {
        // Empty query: use backend search term directly
        setCurrentSearchQuery(llmSearchTerm!);
        isFirstSearchRef.current = false;
      } else if (isFirstSearchRef.current) {
        setCurrentSearchQuery(`${SEARCH_TERM_SUFFIX} ${trimmedQuery}`);
        isFirstSearchRef.current = false;
      } else {
        setCurrentSearchQuery(
          isApparel ? `${trimmedQuery} ${SEARCH_TERM_SUFFIX}` : trimmedQuery
        );
      }
    },
    [llmSearchTerm, isApparel, SEARCH_TERM_SUFFIX]
  );

  // Cleanup is handled by useFilePreview and useRfqConversation hooks

  // Sync selectedImages with pendingFiles - memoize to prevent unnecessary updates
  const pendingFilesMap = useMemo(() => {
    const map = new Map();

    pendingFiles.forEach((pf) => {
      if (pf.id) map.set(pf.id, pf);
      if (pf.file) {
        const key = `${pf.file.name}-${pf.file.size}-${pf.file.lastModified}`;

        map.set(key, pf);
      }
    });

    return map;
  }, [pendingFiles]);

  useEffect(() => {
    setSelectedImages((prevImages) => {
      if (prevImages.length === 0) return prevImages;

      // Find pendingFiles that don't have a corresponding image yet
      const existingPendingIds = new Set(
        prevImages
          .filter((img) => img.pendingFileId)
          .map((img) => img.pendingFileId!)
      );
      const newPendingFiles = pendingFiles.filter(
        (pf) => !existingPendingIds.has(pf.id)
      );

      let hasChanges = false;
      const result: SelectedImage[] = [];

      for (const img of prevImages) {
        // Get preview from ref if it exists, otherwise use the one from image
        const previewUrl = getPreview(img.id) || img.preview;

        if (previewUrl && !previewUrlsRef.current.has(img.id)) {
          setPreview(img.id, previewUrl);
        }

        let updatedImage: SelectedImage = {
          ...img,
          preview: previewUrl || img.preview
        };

        // Map pendingFileId if missing and file exists
        if (!updatedImage.pendingFileId && img.file) {
          const matchingPendingFile = newPendingFiles.find(
            (pf) =>
              pf.file === img.file ||
              (pf.file.name === img.file!.name &&
                pf.file.size === img.file!.size &&
                pf.file.lastModified === img.file!.lastModified)
          );

          if (matchingPendingFile) {
            hasChanges = true;
            updatedImage = {
              ...updatedImage,
              pendingFileId: matchingPendingFile.id
            };
          }
        }

        // Sync caption from pendingFiles - only if different
        if (updatedImage.pendingFileId) {
          const pendingFile = pendingFilesMap.get(updatedImage.pendingFileId);

          if (pendingFile && pendingFile.caption !== updatedImage.caption) {
            hasChanges = true;
            updatedImage = {
              ...updatedImage,
              caption: pendingFile.caption
            };
          }
        }

        result.push(updatedImage);
      }

      // Only update state if there are actual changes
      return hasChanges ? result : prevImages;
    });
  }, [pendingFilesMap, pendingFiles, getPreview, setPreview, previewUrlsRef]);

  const rfqDocumentName = rfq?.rfq?.document?.name;

  useEffect(() => {
    if (!isOpen) return;

    if (llmSearchTerm) {
      // LLM term already has site modifier baked in — set directly to avoid
      // fetchImages prepending the suffix a second time.
      setCurrentSearchQuery(llmSearchTerm.substring(0, 150));
      // Store the display term (no site: prefix) so the refine input is clean.
      setUserSearchQuery(stripSitePrefix(llmSearchTerm));
      lastRequestTimeRef.current = Date.now();
      isFirstSearchRef.current = false;
    } else if (rfqDocumentName) {
      // Fallback: run through fetchImages so the suffix logic is applied.
      fetchImages(rfqDocumentName.substring(0, 100));
    }
  }, [isOpen, llmSearchTerm, rfqDocumentName, fetchImages]);

  const handleCloseRefineSearch = useCallback(() => {
    setShowMoreInput(false);
  }, []);

  const handleOpenRefineSearch = useCallback(() => {
    // Pre-populate with the current search query for refining
    setSearchQuery(userSearchQuery);
    setShowMoreInput(true);
  }, [userSearchQuery]);

  const handleSearch = useCallback(() => {
    fetchImages(searchQuery);
    handleCloseRefineSearch();
  }, [searchQuery, fetchImages, handleCloseRefineSearch]);

  // Handle ESC key to close refine search modal
  useEffect(() => {
    if (!showMoreInput) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseRefineSearch();
        // Defer blur to after React re-renders to prevent focus jumping to another input
        setTimeout(() => {
          imagesContainerRef.current?.focus();
        }, 0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showMoreInput, handleCloseRefineSearch]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleImageError = useCallback((imageUrl: string) => {
    setFailedImageUrls((prev) => {
      if (prev.has(imageUrl)) return prev;

      return new Set(prev).add(imageUrl);
    });
  }, []);

  const handleImageLoad = useCallback((imageUrl: string) => {
    setLoadedImageUrls((prev) => {
      if (prev.has(imageUrl)) return prev;

      return new Set(prev).add(imageUrl);
    });
  }, []);

  const handleImageLoadOrSizeCheck = useCallback(
    (imageUrl: string, e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;

      if (
        img.naturalWidth < MIN_IMAGE_WIDTH ||
        img.naturalHeight < MIN_IMAGE_HEIGHT
      ) {
        handleImageError(imageUrl);
      } else {
        handleImageLoad(imageUrl);
      }
    },
    [handleImageError, handleImageLoad]
  );

  // Check for cached images after images change.
  // For images already in the browser cache, neither onLoad nor onError fires
  // (the events happened before React attached its listeners), so we have to
  // inspect the <img> element directly. complete + dimensions above the
  // minimum means it loaded successfully; complete + zero/too-small
  // dimensions (with a non-empty src) means it failed or is unusable, so we
  // mark it as broken instead of leaking through as a broken <img>.
  useEffect(() => {
    if (images.length === 0) return;

    const rafId = requestAnimationFrame(() => {
      const container = imagesContainerRef.current;

      if (!container) return;

      const imgElements = container.querySelectorAll('img');

      imgElements.forEach((img) => {
        if (!img.complete || !img.src) return;
        if (
          img.naturalWidth >= MIN_IMAGE_WIDTH &&
          img.naturalHeight >= MIN_IMAGE_HEIGHT
        ) {
          handleImageLoad(img.src);
        } else {
          handleImageError(img.src);
        }
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [images, handleImageLoad, handleImageError]);

  // Helper to upload image from URL via backend (avoids CORS issues)
  const uploadImageFromUrl = async (
    url: string,
    caption: string
  ): Promise<UploadedFile> => {
    if (!rfq?.rfq?.id) {
      throw new Error('RFQ ID is required');
    }

    const response = await uploadRfqFileFromUrl({
      rfq_id: rfq.rfq.id,
      image_url: url,
      caption: caption || undefined
    });

    return response.file;
  };

  const scrollCaptionListToBottom = useCallback(() => {
    if (captionListRef.current) {
      setTimeout(() => {
        if (captionListRef.current) {
          captionListRef.current.scrollTo({
            top: captionListRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);

  const handleImageClick = (
    e: React.MouseEvent,
    image: ImageSearchResult,
    index: number
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const imageUrl = typeof image === 'string' ? image : image.url;

    // Check if already selected
    const isSelected = selectedImages.some((img) => img.url === imageUrl);

    if (isSelected) {
      // Deselect
      const imageToRemove = selectedImages.find((img) => img.url === imageUrl);

      if (imageToRemove) {
        // Remove from upload hook
        if (imageToRemove.pendingFileId) {
          removeFileFromUpload(imageToRemove.pendingFileId);
        }

        setSelectedImages((prev) =>
          prev.filter((img) => img.id !== imageToRemove.id)
        );
      }
    } else {
      // Create pending image - don't add to selectedImages yet
      const imageId = `${imageUrl}-${Date.now()}-${Math.random()}`;

      const newPendingImage: SelectedImage = {
        url: imageUrl,
        thumbnailUrl: image.thumbnail_url,
        id: imageId,
        pendingFileId: '',
        caption: '',
        originalIndex: index,
        source: 'reference' // Image from reference images
      };

      setPendingImage(newPendingImage);
      // Open caption modal for the new pending image (not edit mode, so button shows "Add +")
      openModalForNewItems(0);
    }
  };

  const handleRemoveSelectedImage = useCallback(
    (imageId: string) => {
      const imageToRemove = selectedImagesRef.current.find(
        (img) => img.id === imageId
      );

      if (!imageToRemove) return;

      // Revoke preview URL if it exists
      removePreview(imageToRemove.id);

      // Remove from upload hook
      if (imageToRemove.pendingFileId) {
        removeFileFromUpload(imageToRemove.pendingFileId);
      }

      setSelectedImages((prev) => {
        const filtered = prev.filter((img) => img.id !== imageId);

        // Close modal if no images left
        if (filtered.length === 0) {
          onCloseCaptionModal();
        } else if (currentCaptionIndex >= filtered.length) {
          // Adjust current index if needed
          captionModal.setCurrentCaptionIndex(filtered.length - 1);
        }

        return filtered;
      });
    },
    [
      removeFileFromUpload,
      removePreview,
      onCloseCaptionModal,
      currentCaptionIndex,
      captionModal
    ]
  );

  // Helper to convert and save caption if needed
  const convertAndSaveCaption = async (
    image: SelectedImage,
    caption: string
  ) => {
    // If already uploaded via URL, update caption via PATCH endpoint
    if (image.uploadedFileId) {
      if (!rfq?.rfq?.id) {
        throw new Error('RFQ ID is required');
      }

      try {
        await updateRfqFileCaption({
          rfq_id: rfq.rfq.id,
          file_id: parseInt(image.uploadedFileId, 10),
          caption: caption || ''
        });

        // Update local state to reflect the new caption and clear loading state
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, caption, isUploading: false, uploadError: undefined }
              : img
          )
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to update caption';

        // Note: handleAxiosError already reports axios failures to Sentry.
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, isUploading: false, uploadError: errorMessage }
              : img
          )
        );
        throw error;
      }

      return;
    }

    // Upload image from URL via backend (avoids CORS) - upload even without caption
    if (!image.file) {
      let uploadedFile: UploadedFile | null = null;
      let lastError: Error | unknown = null;

      // Try main URL first
      try {
        uploadedFile = await uploadImageFromUrl(image.url, caption);
      } catch (error) {
        lastError = error;
        // Don't report yet — we may still recover via the thumbnail fallback below.
        // If both attempts fail, we report the final lastError once.
      }

      // If main URL failed and we have a thumbnail URL, try that as fallback
      if (!uploadedFile && image.thumbnailUrl) {
        try {
          uploadedFile = await uploadImageFromUrl(image.thumbnailUrl, caption);
          lastError = null; // Clear error since fallback succeeded
        } catch (fallbackError) {
          lastError = fallbackError;
        }
      }

      // If upload succeeded (either main URL or fallback)
      if (uploadedFile) {
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? {
                  ...img,
                  uploadedFileId: uploadedFile!.id.toString(),
                  uploadedFile: uploadedFile!,
                  caption,
                  isUploading: false,
                  uploadError: undefined
                }
              : img
          )
        );
      } else {
        // Both attempts failed, show error
        const errorMessage =
          lastError instanceof Error
            ? lastError.message
            : 'Failed to upload image';

        // Note: handleAxiosError already reports axios failures to Sentry.
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, isUploading: false, uploadError: errorMessage }
              : img
          )
        );
        throw lastError;
      }
    } else if (image.file) {
      // File already exists (shouldn't happen for URL images, but handle it)
      const pendingFile = getPendingFileByIdOrFile(
        image.pendingFileId,
        image.file
      );

      if (pendingFile) {
        await updateCaptionForFile(pendingFile, caption).catch(() => {
          // Silently handle errors
        });
      }
    }
  };

  // Add pending image/file to selectedImages and start upload
  const addPendingImage = useCallback(async () => {
    // Handle pending image (from search results)
    if (pendingImage) {
      const imageToUpload = {
        ...pendingImage,
        caption: captionValue,
        isUploading: true
      };

      setSelectedImages((prev) => {
        const updated = [...prev, imageToUpload];

        return updated;
      });
      setPendingImage(null);

      // Start upload in background (even without caption)
      convertAndSaveCaption(imageToUpload, captionValue).catch(() => {});

      // Scroll caption list to bottom to show the newly added image
      scrollCaptionListToBottom();

      return;
    }

    // Handle pending files (from file upload)
    if (pendingFileUploads.length > 0) {
      const currentPendingFile = pendingFileUploads[currentCaptionIndex];

      if (!currentPendingFile || !currentPendingFile.file) return;

      const fileToAdd = currentPendingFile.file;

      // Add file to upload hook (creates pending file with empty caption)
      addFilesToUpload([fileToAdd]);

      // Add to selectedImages with caption - the useEffect will map pendingFileId
      const imageToAdd = {
        ...currentPendingFile,
        caption: captionValue
      };

      setSelectedImages((prev) => {
        const updated = [...prev, imageToAdd];

        return updated;
      });

      // Remove from pendingFileUploads
      setPendingFileUploads((prev) => {
        const updated = prev.filter(
          (_, index) => index !== currentCaptionIndex
        );

        return updated;
      });

      // Find and update the pending file's caption (this will trigger upload if caption is provided)
      // Use a retry mechanism since React state updates are async
      const updatePendingFileCaption = async (retries = 5) => {
        for (let i = 0; i < retries; i++) {
          const pendingFile = getPendingFileByIdOrFile(undefined, fileToAdd);

          if (pendingFile) {
            // Update caption first
            await updateCaptionForFile(pendingFile, captionValue).catch(() => {
              // Silently handle errors
            });

            // If no caption, trigger upload directly (updateCaptionForFile only uploads if caption exists)
            if (!captionValue.trim() && pendingFile.status === 'pending') {
              await uploadFileWithCaption(pendingFile.id, captionValue).catch(
                () => {
                  // Silently handle errors
                }
              );
            }

            return;
          }
          // Wait a bit before retrying
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      };

      updatePendingFileCaption();

      // Scroll caption list to bottom to show the newly added file
      scrollCaptionListToBottom();
    }
  }, [
    pendingImage,
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    scrollCaptionListToBottom,
    addFilesToUpload,
    getPendingFileByIdOrFile,
    updateCaptionForFile,
    uploadFileWithCaption
  ]);

  // Unified caption save handler that handles both URL-based and file-based images
  // Note: This does NOT add pending images/files - use addPendingImage() for that
  const handleCaptionSave = useCallback(async () => {
    // Don't save if this is a pending image or file (only add when "Add" is clicked)
    if (pendingImage || pendingFileUploads.length > 0) {
      return;
    }

    const currentImage = selectedImages[currentCaptionIndex];

    if (!currentImage) return;

    // Handle file-based images
    if (currentImage.file) {
      const pendingFile = getPendingFileByIdOrFile(
        currentImage.pendingFileId,
        currentImage.file
      );

      if (pendingFile) {
        await updateCaptionForFile(pendingFile, captionValue).catch(() => {
          // Silently handle errors
        });
      }
    } else {
      // URL-based image handling - upload even without caption
      const needsUpload = !currentImage.uploadedFileId;

      if (needsUpload) {
        // Set uploading state first (synchronously)
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === currentImage.id
              ? { ...img, isUploading: true, uploadError: undefined }
              : img
          )
        );

        // Start upload (don't await - let it run in background)
        convertAndSaveCaption(currentImage, captionValue).catch(() => {});

        // Small delay to ensure state update is visible
        await new Promise((resolve) => setTimeout(resolve, 100));
      } else if (currentImage.uploadedFileId) {
        // If already uploaded, just update the caption (no loading state needed)
        await convertAndSaveCaption(currentImage, captionValue).catch(() => {});
      }
    }
  }, [
    selectedImages,
    pendingImage,
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    getPendingFileByIdOrFile,
    updateCaptionForFile
  ]);

  const handleCaptionNext = useCallback(async () => {
    // If this is a pending image, add it and close modal
    if (pendingImage) {
      await addPendingImage();
      onCloseCaptionModal();

      return;
    }

    // If this is a pending file, add it and check if there are more
    if (pendingFileUploads.length > 0) {
      const hasMoreFiles = pendingFileUploads.length > 1;

      await addPendingImage();

      // If there are more pending files, move to next; otherwise close modal
      if (hasMoreFiles) {
        // Move to next pending file in the carousel (index stays the same since we removed current)
        setCaptionValue('');
      } else {
        // No more pending items, close modal
        setPendingFileUploads([]);
        onCloseCaptionModal();
      }

      return;
    }

    // For existing images, save caption and move to next
    await handleCaptionSave();
    await captionNext();
  }, [
    handleCaptionSave,
    captionNext,
    pendingImage,
    pendingFileUploads,
    currentCaptionIndex,
    addPendingImage,
    onCloseCaptionModal,
    setCaptionValue
  ]);

  const handleCaptionPrevious = useCallback(async () => {
    await handleCaptionSave();
    await captionPrevious();
  }, [handleCaptionSave, captionPrevious]);

  const handleCaptionDone = useCallback(async () => {
    // If this is a pending image or file, add it when "Add" is clicked
    if (pendingImage || pendingFileUploads.length > 0) {
      await addPendingImage();
      setPendingFileUploads([]);
      onCloseCaptionModal();

      return;
    }

    // For existing images, save caption and close
    await handleCaptionSave();
    captionDone();
  }, [
    handleCaptionSave,
    captionDone,
    pendingImage,
    pendingFileUploads,
    addPendingImage,
    onCloseCaptionModal
  ]);

  const handleCaptionModalClose = useCallback(async () => {
    // Clear pending image/files if modal is closed without adding (don't save, don't add)
    // Note: onSave is already called by CaptionModal before onClose, so we don't need to save again
    if (pendingImage) {
      setPendingImage(null);
      setCaptionValue('');
    }
    if (pendingFileUploads.length > 0) {
      // Revoke preview URLs for pending files
      pendingFileUploads.forEach((file) => {
        if (file.id) {
          removePreview(file.id);
        }
      });
      setPendingFileUploads([]);
      setCaptionValue('');
    }
    onCloseCaptionModal();
  }, [
    pendingImage,
    pendingFileUploads,
    onCloseCaptionModal,
    setCaptionValue,
    removePreview
  ]);

  const handleSend = async () => {
    // In builder mode with no images, just close the drawer
    if (mode === 'builder' && selectedImages.length === 0) {
      handleClose();

      return;
    }

    if (!rfq?.rfq?.id || selectedImages.length === 0) {
      addUserMessage('I do not have any references');
      connectToConversation('I do not have any references', [], false);
      onSuccess?.();

      return;
    }

    // Upload any images without uploadedFileId (user skipped captions or didn't add them)
    const imagesToUpload = selectedImages.filter(
      (img) => !img.uploadedFileId && !img.file
    );

    if (imagesToUpload.length > 0) {
      try {
        for (const image of imagesToUpload) {
          const uploadedFile = await uploadImageFromUrl(
            image.url,
            image.caption
          );

          setSelectedImages((prev) =>
            prev.map((img) =>
              img.id === image.id
                ? {
                    ...img,
                    uploadedFileId: uploadedFile.id.toString(),
                    uploadedFile
                  }
                : img
            )
          );
        }
      } catch {
        // Note: handleAxiosError already reports axios failures to Sentry.
        // Per-image errors are surfaced in the UI via the uploadError state.
        return;
      }
    }

    // Save any pending caption if modal is open
    if (isCaptionModalOpen && selectedImages[currentCaptionIndex]) {
      await handleCaptionSave();
    }

    setIsSending(true);

    try {
      // Collect file IDs from both sources:
      // 1. Images uploaded via URL (have uploadedFileId)
      const urlUploadedFileIds = selectedImages
        .filter((img) => img.uploadedFileId)
        .map((img) => img.uploadedFileId!);

      // 2. Files uploaded via File upload hook (wait for them to complete)
      const uploadedFileIds = await waitForUploads();

      const hasErrors = pendingFiles.some((pf) => pf.status === 'error');

      if (hasErrors) {
        setIsSending(false);

        return;
      }

      // Combine both sets of file IDs
      const allFileIds = [...urlUploadedFileIds, ...uploadedFileIds];

      // In builder mode, use callback instead of sending conversation messages
      if (mode === 'builder') {
        // Collect full UploadedFile objects from both sources
        const urlUploadedFiles = selectedImages
          .filter((img) => img.uploadedFile)
          .map((img) => img.uploadedFile!);
        const fileUploadedFiles = pendingFiles
          .filter((pf) => pf.status === 'completed' && pf.uploadedFile)
          .map((pf) => pf.uploadedFile!);
        const allUploadedFiles = [...urlUploadedFiles, ...fileUploadedFiles];

        setIsSending(false);
        onImagesSelected?.(allUploadedFiles);
        handleClose();

        return;
      }

      // Standalone mode: send conversation messages
      // The backend conversation model requires two turns:
      // 1. An initial message to start the conversation
      // 2. A follow-up message with the file references
      // We await the first call so the backend processes it before the second.
      const firstMessage = rfq?.rfq?.document?.name
        ? `Let's get started making an RFQ for a ${rfq.rfq.document.name}`
        : 'Here are my inspiration images';

      await connectToConversation(firstMessage, [], false);

      // Now send the files as a second turn and navigate immediately
      const filesMessage = rfq?.rfq?.document?.name
        ? `Here are my inspiration images for ${rfq.rfq.document.name}`
        : 'Here are my inspiration images';

      // Collect UploadedFile objects so the chat can display file previews.
      // URL-based images: construct from selectedImage data
      // File-based uploads: use the full UploadedFile from the upload hook
      const uploadedFiles: UploadedFile[] = [];

      for (const img of selectedImages) {
        if (img.uploadedFileId) {
          // URL-based image uploaded via backend
          uploadedFiles.push({
            id: parseInt(img.uploadedFileId, 10),
            url: img.preview || img.url,
            name: img.caption || `Image ${img.originalIndex + 1}`,
            size: 0,
            content_type: 'image/jpeg',
            description: img.caption || null,
            user_id: 0,
            created_at: new Date().toISOString(),
            tags: []
          });
        } else if (img.file && img.pendingFileId) {
          // File-based upload — get the full UploadedFile from the hook
          const pf = pendingFiles.find((p) => p.id === img.pendingFileId);

          if (pf?.uploadedFile) {
            uploadedFiles.push(pf.uploadedFile);
          }
        }
      }

      addUserMessage(filesMessage, uploadedFiles);
      connectToConversation(filesMessage, allFileIds, false);

      analyticsService.trackEvent(RFQ_BUILDER_IMAGE_SELECTION_COMPLETED, {
        rfq_id: rfq?.rfq?.id,
        image_count: selectedImages.length
      });
      onSuccess?.();
    } catch (error) {
      captureException({
        error:
          error instanceof Error ? error : new Error('Failed to send images'),
        extra: {
          location: 'RfqImageSelection.handleSend',
          step: 'sendImages',
          rfqId: rfq?.rfq?.id,
          mode,
          selectedImagesCount: selectedImages.length,
          pendingFilesCount: pendingFiles.length
        }
      });
      setIsSending(false);
    }
  };

  const handleAddOwnImages = () => {
    // Trigger file input instead of navigating
    fileInputRef.current?.click();
  };

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileArray = Array.from(selectedFiles);

    // Create SelectedImage entries with unique IDs (but don't add to selectedImages yet)
    const timestamp = Date.now();
    const newPendingFiles: SelectedImage[] = fileArray.map((file, index) => {
      const imageId = `${file.name}-${file.size}-${file.lastModified}-${timestamp}-${index}-${Math.random()}`;
      const preview = URL.createObjectURL(file);

      // Store preview in ref immediately
      setPreview(imageId, preview);

      return {
        url: preview, // Use preview URL for file-based images
        id: imageId,
        file,
        pendingFileId: '', // Will be set when added to selectedImages
        caption: '',
        originalIndex: index,
        preview,
        source: 'user' // File uploaded by user
      };
    });

    // Store in pendingFileUploads instead of immediately adding to selectedImages
    setPendingFileUploads(newPendingFiles);

    // Open caption modal for new files (start at index 0 since they're pending)
    openModalForNewItems(0);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const imageDelays = useMemo(() => {
    if (images.length === 0) return [];
    const indices = Array.from({ length: images.length }, (_, i) => i);
    const shuffled = indices.sort(() => Math.random() - 0.5);

    return shuffled.map((_, index) => index * 0.03 + Math.random() * 0.08);
  }, [images.length]);

  // Render preview for caption modal
  const renderPreview = useCallback(
    (image: SelectedImage, previewUrl: string | undefined) => {
      if (!previewUrl) return null;

      return (
        <motion.div
          key={`${image.id}-${currentCaptionIndex}`}
          layoutId={`image-${image.url}`}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{
            duration: 0.5,
            delay: 0.25,
            ease: [0.25, 0.1, 0.25, 1],
            layout: {
              type: 'spring',
              stiffness: 400,
              damping: 35
            }
          }}
          className="h-full"
        >
          <img
            draggable={false}
            src={previewUrl}
            alt={`Selected ${currentCaptionIndex + 1}`}
            className="h-full w-auto object-contain rounded-2xl border border-gray-4 bg-gray-2"
            onError={() => {
              // Image failed to load - preview URL may have been revoked
            }}
          />
        </motion.div>
      );
    },
    [currentCaptionIndex]
  );

  const getPreviewUrl = useCallback(
    (image: SelectedImage) => {
      return getPreview(image.id) || image.preview || image.url;
    },
    [getPreview]
  );

  return (
    <>
      <Drawer
        isKeyboardDismissDisabled
        motionProps={{
          initial: { x: '100%' },
          animate: { x: 0 },
          exit: { x: '100%' },
          transition: {
            type: 'tween',
            duration: 0.4,
            ease: [0.4, 0, 0.2, 1]
          }
        }}
        hideCloseButton
        size="full"
        placement="right"
        isOpen={isOpen}
        onClose={handleClose}
        scrollBehavior="inside"
      >
        <DrawerContent className="relative h-dvh w-full mobile:overflow-y-auto p-0">
          {/* Top decorative div */}
          <div
            style={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 49,
              backgroundColor: 'transparent',
              backgroundImage: 'radial-gradient(transparent 1px, white 1px)',
              backgroundSize: '4px 4px',
              backdropFilter: 'blur(3px)',
              mask: 'linear-gradient(#000 calc(100% - 20px), #0000)'
            }}
          />

          <div className="h-16 bg-transparent mobile:bg-white mobile:left-0 mobile:right-0 flex items-center gap-2 px-5 fixed top-0 z-[50]">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={handleClose}
            >
              <ChevronLeftIcon className="size-5" />
              <Typography variant="body-1" weight="bold">
                Back
              </Typography>
            </div>
          </div>

          <div className="h-full flex flex-col">
            <div className="flex-1 mobile:px-6 px-[120px] gap-10 grid grid-cols-10 mobile:grid-cols-1">
              {/* // left side */}
              <div className="flex flex-col gap-6 sticky top-[14%] self-start col-span-4">
                <Typography as="h1" variant="h3" weight="bold">
                  Choose reference images
                </Typography>
                <Typography variant="body-1">
                  Pick any examples that resemble your product,
                  <br />
                  and tell us what you like.
                </Typography>

                <div
                  ref={captionListRef}
                  className="min-h-[340px] max-h-[340px] overflow-y-auto cavela-scrollbar"
                  style={{
                    backgroundImage: `
            linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
          `,
                    backgroundSize: '40px 40px',
                    backgroundColor: '#FFF'
                  }}
                >
                  {selectedImages.length > 0 ? (
                    <div className="flex flex-col gap-3">
                      <AnimatePresence>
                        {selectedImages.map((selectedImage, index) => {
                          const pendingFile = pendingFiles.find(
                            (pf) => pf.id === selectedImage.pendingFileId
                          );
                          // Find the current modal item and check if it matches this selectedImage
                          const currentModalItem =
                            modalItems[currentCaptionIndex];
                          const isSelected =
                            isCaptionModalOpen &&
                            currentModalItem &&
                            currentModalItem.id === selectedImage.id;

                          return (
                            <motion.div
                              key={selectedImage.id}
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              layout
                              transition={{
                                layout: {
                                  duration: 0.3,
                                  ease: [0.4, 0, 0.2, 1]
                                },
                                opacity: { duration: 0.2, ease: 'easeOut' },
                                y: { duration: 0.2, ease: 'easeOut' }
                              }}
                              className={cn(
                                'flex items-center gap-4 p-4 bg-gray-1 rounded-xl cursor-pointer hover:bg-gray-2 transition-colors overflow-hidden',
                                isSelected && 'bg-gray-4'
                              )}
                              onClick={() => handleEditCaption(index)}
                            >
                              {isImageFile(selectedImage) ? (
                                <img
                                  src={
                                    selectedImage.preview || selectedImage.url
                                  }
                                  alt={`Selected ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg"
                                  onError={() => {
                                    // Fallback to URL if preview fails
                                  }}
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-2 rounded-lg flex items-center justify-center">
                                  <DocumentIcon className="size-8 text-gray-500" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                {selectedImage.caption ? (
                                  <Typography variant="body-2" weight="medium">
                                    {selectedImage.caption}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="body-2"
                                    className="text-secondary-text"
                                  >
                                    No caption
                                  </Typography>
                                )}
                                {selectedImage.file && (
                                  <Typography
                                    variant="body-2"
                                    className="text-secondary-text truncate"
                                  >
                                    {selectedImage.file.name}
                                  </Typography>
                                )}

                                {selectedImage.isUploading && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <div className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                    <Typography
                                      variant="body-2"
                                      className="text-secondary-text"
                                    >
                                      Sending image
                                    </Typography>
                                  </div>
                                )}
                                {selectedImage.uploadError && (
                                  <Typography
                                    variant="body-2"
                                    className="text-danger mt-1"
                                  >
                                    Upload failed: {selectedImage.uploadError}
                                  </Typography>
                                )}
                                {pendingFile?.status === 'uploading' && (
                                  <div className="mt-2">
                                    <Progress
                                      value={pendingFile.progress}
                                      size="sm"
                                    />
                                  </div>
                                )}
                                {pendingFile?.status === 'error' && (
                                  <UploadFailedNotice
                                    error={pendingFile.error}
                                    onRetry={() => retryUpload(pendingFile.id)}
                                    onRemove={() =>
                                      handleRemoveSelectedImage(
                                        selectedImage.id
                                      )
                                    }
                                  />
                                )}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveSelectedImage(selectedImage.id);
                                }}
                                className="flex-shrink-0 p-2 hover:bg-gray-3 rounded-full transition-colors"
                                aria-label="Remove image"
                              >
                                <XMarkIcon className="size-5 text-gray-600" />
                              </button>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center justify-between gap-4 mb-8 mobile:mb-0">
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <ButtonV2
                      onClick={handleAddOwnImages}
                      size="lg"
                      color="tertiary"
                      className="h-14 rounded-full px-6"
                    >
                      <PlusIcon className="size-5" />
                      <Typography variant="body-1" weight="bold">
                        Add your own images
                      </Typography>
                    </ButtonV2>
                  </div>
                  <ButtonV2
                    onPress={handleSend}
                    isDisabled={
                      isSending ||
                      isUploadingFiles ||
                      selectedImages.some((img) => img.isUploading === true) ||
                      (mode === 'builder' && selectedImages.length === 0)
                    }
                    isLoading={isSending}
                    size="lg"
                    className="w-36 h-14 rounded-full px-6"
                  >
                    {mode === 'builder'
                      ? 'Submit'
                      : selectedImages.length > 0
                        ? 'Submit'
                        : 'Skip'}
                  </ButtonV2>
                </div>
              </div>

              {/* right side */}
              <div
                ref={imagesContainerRef}
                tabIndex={-1}
                style={{
                  backgroundImage: `
          linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
        `,
                  backgroundSize: '40px 40px',
                  backgroundColor: '#FFF'
                }}
                className="flex flex-col gap-4 relative h-full col-span-6 pt-10 px-6 outline-none"
              >
                <div className="relative w-full pb-20">
                  {images.length > 0 ? (
                    <div className="relative">
                      <div className="grid grid-cols-3 gap-3">
                        {images
                          .filter((image) => {
                            const url =
                              typeof image === 'string' ? image : image.url;

                            return !failedImageUrls.has(url);
                          })
                          .map((image, index) => {
                            const imageUrl =
                              typeof image === 'string' ? image : image.url;

                            const isSelected = selectedImages.some(
                              (img) => img.url === imageUrl
                            );

                            const isFirstColumn =
                              index % 3 === 0 && index !== 0;
                            const isLastColumn = index % 3 === 2 && index !== 2;
                            const marginTop =
                              index === 1
                                ? '100px'
                                : index === 2
                                  ? '42px'
                                  : isLastColumn
                                    ? '-56px'
                                    : isFirstColumn
                                      ? '-100px'
                                      : undefined;

                            const isLoaded = loadedImageUrls.has(imageUrl);

                            // Check if this image is currently being viewed in the modal (including pending)
                            const isInModal =
                              isCaptionModalOpen &&
                              (modalItems[currentCaptionIndex]?.url ===
                                imageUrl ||
                                pendingImage?.url === imageUrl);

                            return (
                              <motion.div
                                key={imageUrl}
                                layoutId={
                                  isSelected || isInModal
                                    ? `image-${imageUrl}`
                                    : undefined
                                }
                                initial={{ opacity: 0 }}
                                animate={{
                                  opacity: isInModal ? 0 : 1,
                                  scale: isInModal ? 0.98 : 1
                                }}
                                transition={{
                                  duration: 0.7,
                                  delay: imageDelays[index] || 0,
                                  ease: [0.4, 0, 0.2, 1],
                                  layout: {
                                    type: 'spring',
                                    stiffness: 400,
                                    damping: 35
                                  },
                                  opacity: {
                                    duration: isInModal ? 0.15 : 0.7,
                                    ease: [0.4, 0, 0.2, 1]
                                  },
                                  scale: {
                                    duration: 0.15,
                                    ease: [0.4, 0, 0.2, 1]
                                  }
                                }}
                                onClick={(e) =>
                                  handleImageClick(e, image, index)
                                }
                                className={cn(
                                  'border border-gray-4 relative overflow-hidden cursor-pointer hover:opacity-80 transition-all bg-gray-2 rounded-xl',
                                  isSelected
                                    ? 'shadow-[0_4px_6px_-1px_rgba(0,14,239,0.3),0_2px_4px_-1px_rgba(0,14,239,0.2)] border-primary'
                                    : '',
                                  isInModal && 'pointer-events-none z-50'
                                )}
                                style={{
                                  height: '269px',
                                  ...(marginTop && { marginTop })
                                }}
                              >
                                <div className="relative w-full h-full">
                                  {/* Blurred placeholder background */}
                                  <motion.div
                                    className="absolute inset-0 w-full h-full rounded-lg"
                                    initial={{
                                      filter: 'blur(20px)',
                                      scale: 1.1
                                    }}
                                    animate={{
                                      filter: isLoaded
                                        ? 'blur(0px)'
                                        : 'blur(20px)',
                                      scale: isLoaded ? 1 : 1.1
                                    }}
                                    transition={{
                                      duration: 0.8,
                                      ease: [0.4, 0, 0.2, 1]
                                    }}
                                    style={{
                                      backgroundImage: `url(${imageUrl})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center'
                                    }}
                                  />
                                  {/* Sharp image overlay */}
                                  <motion.div
                                    className="absolute inset-0 w-full h-full"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: isLoaded ? 1 : 0 }}
                                    transition={{
                                      duration: 0.6,
                                      ease: [0.4, 0, 0.2, 1]
                                    }}
                                  >
                                    <img
                                      draggable={false}
                                      src={imageUrl}
                                      alt={
                                        imageUrl || `Search result ${index + 1}`
                                      }
                                      className="w-full h-full object-cover max-w-full max-h-full"
                                      onLoad={(e) =>
                                        handleImageLoadOrSizeCheck(imageUrl, e)
                                      }
                                      onError={() => handleImageError(imageUrl)}
                                    />
                                  </motion.div>
                                </div>
                              </motion.div>
                            );
                          })}
                      </div>

                      {/* See more button */}
                      {!showMoreInput && (
                        <div className="sticky bottom-10 flex justify-end mt-4 z-10">
                          <ButtonV2
                            value={searchQuery}
                            onPress={handleOpenRefineSearch}
                            size="lg"
                            className="h-16 rounded-full px-5 text-dark border border-white bg-[#F9FAFB66]/80 backdrop-blur-md shadow-md hover:!opacity-100 hover:!bg-[#F9FAFB66]/85 hover:shadow-[0_4px_20px_rgba(0,14,239,0.15)] !transition-all"
                            endContent={
                              <div className="min-h-10 max-h-10 min-w-10 max-w-10 bg-white rounded-full flex items-center justify-center">
                                {imageSearch.isFetching ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <mask
                                      id="mask0_2561_6640"
                                      style={{ maskType: 'alpha' }}
                                      maskUnits="userSpaceOnUse"
                                      x="0"
                                      y="0"
                                      width="24"
                                      height="24"
                                    >
                                      <rect
                                        width="24"
                                        height="24"
                                        fill="#D9D9D9"
                                      />
                                    </mask>
                                    <g mask="url(#mask0_2561_6640)">
                                      <path
                                        d="M4.4 21L3 19.6L10.525 12.05L6 10.925L10.95 7.85L10.525 2L15 5.775L20.4 3.575L18.225 9L22 13.45L16.15 13.05L13.05 18L11.925 13.475L4.4 21ZM5 8L3 6L5 4L7 6L5 8ZM13.875 12.925L15.075 10.95L17.4 11.125L15.9 9.35L16.775 7.2L14.625 8.075L12.85 6.6L13.025 8.9L11.05 10.125L13.3 10.675L13.875 12.925ZM18 21L16 19L18 17L20 19L18 21Z"
                                        fill="currentColor"
                                      />
                                    </g>
                                  </svg>
                                )}
                              </div>
                            }
                          >
                            Refine search
                          </ButtonV2>
                        </div>
                      )}
                    </div>
                  ) : rateLimitError ||
                    (imageSearch.isError && currentSearchQuery) ? (
                    <div className="flex flex-col items-center justify-center h-full gap-2">
                      <Typography
                        variant="body-1"
                        weight="medium"
                        className="text-red-600"
                      >
                        {rateLimitError ||
                          imageSearch.error?.message ||
                          'Failed to fetch images'}
                      </Typography>
                      <Typography variant="body-2" className="text-gray-6">
                        Please try again later
                      </Typography>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full min-h-dvh">
                      <BlobV2
                        speed={0.2}
                        numBlobs={6}
                        isolation={70}
                        lookAt={{ x: 0, y: 0, z: 0 }}
                        zoom={1.5}
                      />
                    </div>
                  )}
                </div>

                {/* Search input overlay */}
                <AnimatePresence>
                  {showMoreInput && images.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                      className="fixed mobile:inset-0 mobile:top-0 mobile:left-0 mobile:right-0 mobile:bottom-0 top-0 right-0 bottom-0 w-[58%] mobile:w-full z-[9999] flex items-center justify-center"
                      onClick={(e) => {
                        // Close input if clicking outside the input container
                        if (e.target === e.currentTarget) {
                          handleCloseRefineSearch();
                        }
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: 0.6,
                          delay: 0.1,
                          ease: [0.4, 0, 0.2, 1]
                        }}
                        className="cursor-pointer relative bg-[#F9FAFB66]/30 backdrop-blur-md shadow-none w-full h-full mobile:rounded-none p-6 mobile:pr-6 pr-[140px] flex flex-col items-center justify-center gap-6 overflow-y-auto"
                        onClick={(e) => {
                          handleCloseRefineSearch();
                          e.stopPropagation();
                        }}
                      >
                        {/* Close button */}
                        <button
                          onClick={handleCloseRefineSearch}
                          className="absolute top-5 right-5 p-2 hover:bg-white/20 rounded-full transition-colors z-10"
                          aria-label="Close"
                        >
                          <XMarkIcon className="size-8 text-gray-700" />
                        </button>

                        <div
                          className="bg-white rounded-full px-4 py-3 shadow-lg flex items-center gap-3 w-full max-w-md"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={`Photos of ${rfq?.rfq?.document?.name}`}
                            classNames={{
                              input:
                                '!bg-transparent border-none focus:outline-none',
                              inputWrapper:
                                '!bg-transparent border-none shadow-none hover:!bg-transparent focus-within:!bg-transparent'
                            }}
                            className="flex-1"
                            autoFocus
                          />
                          <ButtonV2
                            onPress={handleSearch}
                            isIconOnly
                            size="sm"
                            className="!w-8 !h-8 !min-w-8 !min-h-8 rounded-full bg-primary-text"
                          >
                            <StarIcon className="size-4 text-white" />
                          </ButtonV2>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Bottom decorative div */}
          <div
            style={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: 'transparent',
              backgroundImage: 'radial-gradient(transparent 1px, white 1px)',
              backgroundSize: '4px 4px',
              backdropFilter: 'blur(3px)',
              mask: 'linear-gradient(#0000 0%, #000 20px)'
            }}
          />

          {/* Caption Modal */}
          <CaptionModal
            isOpen={isCaptionModalOpen}
            onClose={handleCaptionModalClose}
            items={modalItems}
            currentIndex={currentCaptionIndex}
            captionValue={captionValue}
            onCaptionChange={setCaptionValue}
            isEditMode={isEditMode}
            onSave={handleCaptionSave}
            onNext={handleCaptionNext}
            onPrevious={handleCaptionPrevious}
            onDone={handleCaptionDone}
            renderPreview={renderPreview}
            getPreviewUrl={getPreviewUrl}
            width="w-[58%]"
            focusAfterClose={imagesContainerRef}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default RfqImageSelection;
